import { useEffect, useRef, useState } from 'react';
import type { ChangeEventHandler, FocusEventHandler, ReactNode } from 'react';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import LocationCascade from '../../../components/LocationCascade';
import TagsInput from '../../../components/TagsInput';
import { CaretIcon, FlagEntryIcon } from '../../../assets/icons';
import { Field, fieldCls } from './formFields';
import PhoneRow from './PhoneRow';
import {
  ELECTION_RESULTS,
  GENDERS,
  PARTIES,
  RELIGIONS,
  RESERVATIONS,
  STATES,
  TITLES,
  toSelectOptions,
  type AddressValues,
  type PaValues,
  type UserFormValues,
} from './types';

type SetField = (field: string, value: unknown) => void;

/** Minister positions offered when adding/editing a minister (single-select Designation). */
const MINISTER_DESIGNATIONS = [
  'Chief Minister',
  'Deputy Chief Minister',
  'Cabinet Minister',
  'Minister of State (Independent Charge)',
  'Minister of State',
];

interface Props {
  values: UserFormValues;
  setFieldValue: SetField;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errorOf: (name: keyof UserFormValues) => string | undefined;
  organization?: number;
  designationOptions: { value: string; label: string }[];
  savedCascade?: { label?: string } | null;
  onLocationRank: (rank?: number) => void;
  /** When opened from the department People tab, the designation is fixed and read-only. */
  lockDesignation?: boolean;
  /** Department-flow: the elected posts from the department's Structure (value = post). */
  deptPosts?: { value: string; label: string; level: string }[];
  /** Department-flow: location options for the selected post's level. */
  locationOptions?: { value: string; label: string }[];
  /** Department-flow: level options (levels with an elected post). */
  levelOptions?: { value: string; label: string }[];
  deptLevel?: string;
  onDeptLevel?: (v: string) => void;
  /** Minister flow: portfolio department options shown below Designation. */
  lockedDeptOptions?: { value: string; label: string }[];
  /** Show the portfolio Department dropdown below Designation (minister flow). */
  showDeptChoice?: boolean;
  /** Government-flow (department not fixed): pick Department → Level → Post → Location. */
  crossDept?: boolean;
  crossDeptOptions?: { value: string; label: string }[];
  crossLevelOptions?: { value: string; label: string }[];
  crossPostOptions?: { value: string; label: string }[];
  govLevel?: string;
  onGovLevel?: (v: string) => void;
}

/** A numbered, collapsible form section (Figma accordion card). */
function AccordionSection({
  index,
  title,
  hint,
  open,
  error,
  onToggle,
  children,
}: {
  index: number;
  title: string;
  hint: string;
  open: boolean;
  /** True when a field inside this section has a validation error. */
  error?: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className={[
        'overflow-hidden rounded-xl border bg-white',
        error ? 'border-red-300' : 'border-[#e2e8f0]',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[#f8fafc]"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={[
              'flex size-6 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold',
              error ? 'bg-[#fee2e2] text-[#dc2626]' : 'bg-[#f1f5f9] text-[#475569]',
            ].join(' ')}
          >
            {index}
          </span>
          <span className="truncate text-[12px] font-bold uppercase tracking-[0.4px] text-[#334155]">
            {title}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span
            className={[
              'hidden text-[11px] md:block',
              error ? 'font-medium text-[#dc2626]' : 'text-[#94a3b8]',
            ].join(' ')}
          >
            {error ? 'Required fields missing' : hint}
          </span>
          <CaretIcon
            className={[
              'size-4 transition-transform',
              error ? 'text-[#dc2626]' : 'text-[#94a3b8]',
              open ? 'rotate-180' : '',
            ].join(' ')}
          />
        </div>
      </button>
      {open && <div className="border-t border-[#f1f5f9] px-5 py-5">{children}</div>}
    </div>
  );
}

/** One residential/official address block (Figma: bordered card + colored dot). */
function AddressCard({
  dotColor,
  title,
  path,
  value,
  contactPlaceholder,
  setFieldValue,
}: {
  dotColor: string;
  title: string;
  path: string;
  value: AddressValues;
  contactPlaceholder: string;
  setFieldValue: SetField;
}) {
  const set = (key: keyof AddressValues, v: string) => setFieldValue(`${path}.${key}`, v);
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#e2e8f0] bg-white p-[17px]">
      <div className="flex items-center gap-2">
        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
        <span className="text-sm font-semibold text-[#1e293b]">{title}</span>
      </div>
      <input
        value={value.line}
        onChange={(e) => set('line', e.target.value)}
        placeholder="House/Flat No, Landmark, Locality"
        className={fieldCls()}
      />
      <div className="grid grid-cols-3 gap-2">
        <input
          value={value.city}
          onChange={(e) => set('city', e.target.value)}
          placeholder="District/City"
          className={`${fieldCls()} min-w-0`}
        />
        <Select
          className="w-full"
          placeholder="State"
          showSearch
          optionFilterProp="label"
          value={value.state || undefined}
          onChange={(v) => set('state', v ?? '')}
          options={toSelectOptions(STATES)}
          allowClear
        />
        <input
          value={value.pin}
          onChange={(e) => set('pin', e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="PIN Code"
          className={`${fieldCls()} min-w-0`}
        />
      </div>
      <input
        value={value.contact}
        onChange={(e) => set('contact', e.target.value)}
        placeholder={contactPlaceholder}
        className={fieldCls()}
      />
    </div>
  );
}

/** One PA contact block (Figma: bordered card + PA-01/PA-02 badge). */
function PaCard({
  title,
  badge,
  path,
  value,
  namePlaceholder,
  designationPlaceholder,
  setFieldValue,
}: {
  title: string;
  badge: string;
  path: string;
  value: PaValues;
  namePlaceholder: string;
  designationPlaceholder: string;
  setFieldValue: SetField;
}) {
  const set = (key: keyof PaValues, v: string) => setFieldValue(`${path}.${key}`, v);
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#e2e8f0] bg-white p-[17px]">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-[#1e293b]">{title}</span>
        <span className="rounded bg-[#eef4ff] px-2 py-0.5 text-[10px] font-bold tracking-[0.5px] text-brand">
          {badge}
        </span>
      </div>
      <input
        value={value.name}
        onChange={(e) => set('name', e.target.value)}
        placeholder={namePlaceholder}
        className={fieldCls()}
      />
      <div className="flex gap-2">
        <input
          value={value.phone}
          onChange={(e) => set('phone', e.target.value)}
          placeholder="Phone Number"
          className={fieldCls()}
        />
        <input
          value={value.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder="Email Address"
          className={fieldCls()}
        />
      </div>
      <input
        value={value.designation}
        onChange={(e) => set('designation', e.target.value)}
        placeholder={designationPlaceholder}
        className={fieldCls()}
      />
    </div>
  );
}

export default function PoliticalPersonForm({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  errorOf,
  organization,
  designationOptions,
  savedCascade,
  onLocationRank,
  lockDesignation,
  deptPosts,
  locationOptions,
  levelOptions,
  deptLevel,
  onDeptLevel,
  lockedDeptOptions,
  showDeptChoice,
  crossDept,
  crossDeptOptions,
  crossLevelOptions,
  crossPostOptions,
  govLevel,
  onGovLevel,
}: Props) {
  const deptMode = Boolean(deptPosts && deptPosts.length) || Boolean(levelOptions && levelOptions.length);
  const rolesInputRef = useRef<HTMLInputElement>(null);
  // Section 1 open by default; the rest collapsed.
  const [open, setOpen] = useState<Set<number>>(() => new Set([1]));
  const toggle = (i: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  const setAll = (all: boolean) => setOpen(all ? new Set([1, 2, 3, 4, 5, 6, 7]) : new Set());

  // Which sections currently have a validation error. Mandatory fields live in sections 1
  // (name + mobile) and 3 (DOB + gender).
  const sectionErrors: Record<number, boolean> = {
    1: Boolean(errorOf('firstName') || errorOf('lastName') || errorOf('contact')),
    3: Boolean(errorOf('dob') || errorOf('gender')),
  };
  // Auto-expand any section that has an error (e.g. after a failed submit) so it's visible.
  const errorKey = Object.entries(sectionErrors)
    .filter(([, v]) => v)
    .map(([k]) => k)
    .join(',');
  useEffect(() => {
    if (!errorKey) return;
    setOpen((s) => {
      const n = new Set(s);
      errorKey.split(',').forEach((k) => n.add(Number(k)));
      return n;
    });
  }, [errorKey]);

  const autoFillHome = () => {
    const home = values.addresses.constituencyHome;
    setFieldValue('addresses.constituencyOffice', { ...home });
    setFieldValue('addresses.capitalHome', { ...home });
    setFieldValue('addresses.capitalOffice', { ...home });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Sections toolbar */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#64748b]">Form Sections (7)</span>
        <div className="flex items-center gap-3 text-[11px] font-medium text-brand">
          <button type="button" onClick={() => setAll(true)} className="hover:underline">
            Expand all
          </button>
          <span className="text-[#cbd5e1]">|</span>
          <button type="button" onClick={() => setAll(false)} className="hover:underline">
            Collapse all
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {/* Section 1 — Primary Information */}
        <AccordionSection
          index={1}
          title="Primary Information"
          hint="Name, Contact, Role"
          open={open.has(1)}
          error={sectionErrors[1]}
          onToggle={() => toggle(1)}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row">
              <div className="w-full md:w-[120px]">
                <Field label="Title">
                  <Select
                    className="w-full"
                    placeholder="Shri"
                    value={values.title || undefined}
                    onChange={(v) => setFieldValue('title', v ?? '')}
                    options={toSelectOptions(TITLES)}
                    allowClear
                  />
                </Field>
              </div>
              <Field label="First Name" htmlFor="firstName" required error={errorOf('firstName')}>
                <input
                  id="firstName"
                  name="firstName"
                  placeholder="e.g. Rajesh"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldCls(errorOf('firstName'))}
                />
              </Field>
              <Field label="Middle Name" htmlFor="middleName">
                <input
                  id="middleName"
                  name="middleName"
                  placeholder="e.g. Kumar"
                  value={values.middleName}
                  onChange={handleChange}
                  className={fieldCls()}
                />
              </Field>
              <Field label="Last Name" htmlFor="lastName" required error={errorOf('lastName')}>
                <input
                  id="lastName"
                  name="lastName"
                  placeholder="e.g. Sharma"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldCls(errorOf('lastName'))}
                />
              </Field>
            </div>

            <div className="flex flex-col gap-3 md:flex-row">
              <Field label="Email Address" htmlFor="email" error={errorOf('email')}>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="rajesh.k@chunaav.gov.in"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldCls(errorOf('email'))}
                />
              </Field>
              {!deptMode && !crossDept && (
                <Field label="Elected Position Location" error={errorOf('location')}>
                  <LocationCascade
                    organization={organization}
                    value={values.location}
                    invalid={Boolean(errorOf('location'))}
                    initialLabel={savedCascade?.label}
                    onChange={(sel) => {
                      setFieldValue('location', sel ? String(sel.value) : '');
                      onLocationRank(sel?.levelRank);
                    }}
                  />
                </Field>
              )}
            </div>

            {/* Mobile (repeatable) / Landline+Ext / WhatsApp — shared responsive row. */}
            <PhoneRow
              contact={values.contact}
              landline={values.landline}
              landlineExt={values.landlineExt}
              whatsapp={values.whatsapp}
              whatsappIndex={values.whatsappIndex}
              contactError={errorOf('contact')}
              setFieldValue={setFieldValue}
            />

            {crossDept ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <Field label="Department">
                  <Select
                    className="w-full"
                    placeholder="Select Department"
                    showSearch
                    optionFilterProp="label"
                    value={values.department[0] || undefined}
                    onChange={(v) => {
                      setFieldValue('department', v ? [v] : []);
                      onGovLevel?.('');
                      setFieldValue('designation', []);
                      setFieldValue('location', '');
                      onLocationRank(undefined);
                    }}
                    options={crossDeptOptions}
                    allowClear
                  />
                </Field>
                <Field label="Level name">
                  <Select
                    className="w-full"
                    placeholder={values.department[0] ? 'Select Level' : 'Select a department first'}
                    showSearch
                    optionFilterProp="label"
                    disabled={!values.department[0]}
                    value={govLevel || undefined}
                    onChange={(v) => {
                      onGovLevel?.(v ?? '');
                      setFieldValue('designation', []);
                      setFieldValue('location', '');
                      onLocationRank(undefined);
                    }}
                    options={crossLevelOptions ?? []}
                    allowClear
                  />
                </Field>
                <Field label="Post" error={errorOf('designation')}>
                  <Select
                    className="w-full"
                    placeholder={govLevel ? 'Select Post' : 'Select a level first'}
                    showSearch
                    optionFilterProp="label"
                    disabled={!govLevel}
                    value={values.designation[0] || undefined}
                    onChange={(v) => {
                      setFieldValue('designation', v ? [v] : []);
                      setFieldValue('location', '');
                      onLocationRank(undefined);
                    }}
                    options={crossPostOptions ?? []}
                  />
                </Field>
                <Field label="Elected Position Location" error={errorOf('location')}>
                  <Select
                    className="w-full"
                    placeholder={values.designation[0] ? 'Select location' : 'Select a post first'}
                    showSearch
                    optionFilterProp="label"
                    disabled={!values.designation[0]}
                    value={values.location || undefined}
                    onChange={(v) => {
                      setFieldValue('location', v ?? '');
                      onLocationRank(undefined);
                    }}
                    options={locationOptions ?? []}
                    allowClear
                  />
                </Field>
              </div>
            ) : deptMode ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field label="Level name">
                  <Select
                    className="w-full"
                    placeholder="Select Level"
                    showSearch
                    optionFilterProp="label"
                    value={deptLevel || undefined}
                    onChange={(v) => {
                      onDeptLevel?.(v ?? '');
                      setFieldValue('designation', []);
                      setFieldValue('location', '');
                      onLocationRank(undefined);
                    }}
                    options={levelOptions ?? []}
                    allowClear
                  />
                </Field>
                <Field label="Post" error={errorOf('designation')}>
                  <Select
                    className="w-full"
                    placeholder={deptLevel ? 'Select Post' : 'Select a level first'}
                    showSearch
                    optionFilterProp="label"
                    disabled={!deptLevel}
                    value={values.designation[0] || undefined}
                    // Changing the post resets the location (its options come from the post's level).
                    onChange={(v) => {
                      setFieldValue('designation', v ? [v] : []);
                      setFieldValue('location', '');
                      onLocationRank(undefined);
                    }}
                    options={deptPosts}
                  />
                </Field>
                <Field label="Elected Position Location" error={errorOf('location')}>
                  <Select
                    className="w-full"
                    placeholder={values.designation[0] ? 'Select location' : 'Select a post first'}
                    showSearch
                    optionFilterProp="label"
                    disabled={!values.designation[0]}
                    value={values.location || undefined}
                    onChange={(v) => {
                      setFieldValue('location', v ?? '');
                      onLocationRank(undefined);
                    }}
                    options={locationOptions ?? []}
                    allowClear
                  />
                </Field>
              </div>
            ) : (
              <div className="flex flex-col gap-3 md:flex-row">
                <Field label="Designation" error={errorOf('designation')}>
                  {lockDesignation ? (
                    // Fixed designation (e.g. editing a specific minister row) → read-only.
                    <input
                      value={values.designation.join(', ')}
                      readOnly
                      aria-readonly
                      className="w-full cursor-not-allowed rounded-lg border border-line bg-muted-bg px-3 text-ink"
                    />
                  ) : showDeptChoice ? (
                    // Minister flow (e.g. "+ Add minister"): editable single select of positions.
                    <Select
                      className="w-full"
                      placeholder="Select Designation"
                      showSearch
                      optionFilterProp="label"
                      status={errorOf('designation') ? 'error' : undefined}
                      value={values.designation[0] || undefined}
                      onChange={(v) => setFieldValue('designation', v ? [v] : [])}
                      options={MINISTER_DESIGNATIONS.map((d) => ({ value: d, label: d }))}
                      allowClear
                    />
                  ) : (
                    <Select
                      mode="multiple"
                      className="w-full"
                      placeholder="Select Designation(s)"
                      showSearch
                      optionFilterProp="label"
                      status={errorOf('designation') ? 'error' : undefined}
                      value={values.designation}
                      onChange={(v) => setFieldValue('designation', v)}
                      options={designationOptions}
                    />
                  )}
                </Field>
                {showDeptChoice && (
                  <Field label="Department">
                    <Select
                      mode="multiple"
                      className="w-full"
                      placeholder="Select Department(s)"
                      showSearch
                      optionFilterProp="label"
                      value={values.department}
                      onChange={(v) => setFieldValue('department', v)}
                      options={lockedDeptOptions ?? []}
                    />
                  </Field>
                )}
              </div>
            )}
          </div>
        </AccordionSection>

        {/* Section 2 — Political Party Affiliation & Outcome */}
        <AccordionSection
          index={2}
          title="Political Party Affiliation & Outcome"
          hint="Party, Status, Ministerial Roles"
          open={open.has(2)}
          error={sectionErrors[2]}
          onToggle={() => toggle(2)}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row">
              <Field label="Political Party">
                <Select
                  className="w-full"
                  placeholder="Select Registered Party"
                  showSearch
                  optionFilterProp="label"
                  value={values.politicalParty || undefined}
                  onChange={(v) => setFieldValue('politicalParty', v ?? '')}
                  options={toSelectOptions(PARTIES)}
                  allowClear
                />
              </Field>
              <Field label="Election Result Status">
                <Select
                  className="w-full"
                  placeholder="Winner (Incumbent / Elected)"
                  value={values.electionResult || undefined}
                  onChange={(v) => setFieldValue('electionResult', v ?? '')}
                  options={toSelectOptions(ELECTION_RESULTS)}
                  allowClear
                />
              </Field>
            </div>
            <Field
              label="Ministerial & Standing Committee Roles"
              error={errorOf('ministerialRoles')}
              action={
                <button
                  type="button"
                  onClick={() => rolesInputRef.current?.focus()}
                  className="shrink-0 text-[11px] font-medium text-brand hover:underline"
                >
                  + Add another designation
                </button>
              }
            >
              <TagsInput
                inputRef={rolesInputRef}
                placeholder="e.g. Minister of State for Energy, Member of Committee on Public Undertakings"
                invalid={Boolean(errorOf('ministerialRoles'))}
                value={values.ministerialRoles}
                onChange={(v) => setFieldValue('ministerialRoles', v)}
                showHint={false}
              />
              <span className="mt-1 block text-[11px] text-[#94a3b8]">
                Separate multiple responsibilities and positions using commas.
              </span>
            </Field>
          </div>
        </AccordionSection>

        {/* Section 3 — Demographic Attributes */}
        <AccordionSection
          index={3}
          title="Demographic Attributes"
          hint="DOB, Gender, Category"
          open={open.has(3)}
          error={sectionErrors[3]}
          onToggle={() => toggle(3)}
        >
          <div className="flex flex-col gap-3 md:flex-row">
            <Field label="Date of Birth" required error={errorOf('dob')}>
              <DatePicker
                className="w-full"
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                status={errorOf('dob') ? 'error' : undefined}
                value={values.dob ? dayjs(values.dob) : null}
                onChange={(d) => setFieldValue('dob', d ? d.format('YYYY-MM-DD') : '')}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
              />
            </Field>
            <Field label="Gender" required error={errorOf('gender')}>
              <Select
                className="w-full"
                placeholder="Select Gender"
                status={errorOf('gender') ? 'error' : undefined}
                value={values.gender || undefined}
                onChange={(v) => setFieldValue('gender', v ?? '')}
                options={toSelectOptions(GENDERS)}
                allowClear
              />
            </Field>
            <Field label="Religion">
              <Select
                className="w-full"
                placeholder="Select Religion"
                value={values.religion || undefined}
                onChange={(v) => setFieldValue('religion', v ?? '')}
                options={toSelectOptions(RELIGIONS)}
                allowClear
              />
            </Field>
            <Field label="Caste / Sub-Caste">
              <input
                name="caste"
                placeholder="Community Name"
                value={values.caste}
                onChange={handleChange}
                className={fieldCls()}
              />
            </Field>
            <Field label="Reservation Category">
              <Select
                className="w-full"
                placeholder="General"
                value={values.reservation || undefined}
                onChange={(v) => setFieldValue('reservation', v ?? '')}
                options={toSelectOptions(RESERVATIONS)}
                allowClear
              />
            </Field>
          </div>
        </AccordionSection>

        {/* Section 4 — Standardized Official & Residential Addresses */}
        <AccordionSection
          index={4}
          title="Standardized Official & Residential Addresses"
          hint="4 Locations"
          open={open.has(4)}
          onToggle={() => toggle(4)}
        >
          <div className="flex flex-col gap-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={autoFillHome}
                className="text-xs font-medium text-brand hover:underline"
              >
                Auto-fill same as Home
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <AddressCard
                dotColor="#10b981"
                title="1. Constituency Home Address"
                path="addresses.constituencyHome"
                value={values.addresses.constituencyHome}
                contactPlaceholder="Contact / Mobile Number (Constituency Home)"
                setFieldValue={setFieldValue}
              />
              <AddressCard
                dotColor="#2563eb"
                title="2. Constituency Office Address"
                path="addresses.constituencyOffice"
                value={values.addresses.constituencyOffice}
                contactPlaceholder="Contact / Mobile Number (Constituency Office)"
                setFieldValue={setFieldValue}
              />
              <AddressCard
                dotColor="#f59e0b"
                title="3. Capital Home Address"
                path="addresses.capitalHome"
                value={values.addresses.capitalHome}
                contactPlaceholder="Contact / Mobile Number (Capital Home)"
                setFieldValue={setFieldValue}
              />
              <AddressCard
                dotColor="#2563eb"
                title="4. Capital Office Address"
                path="addresses.capitalOffice"
                value={values.addresses.capitalOffice}
                contactPlaceholder="Contact / Mobile Number (Capital Office)"
                setFieldValue={setFieldValue}
              />
            </div>
          </div>
        </AccordionSection>

        {/* Section 5 — PA Team Contacts */}
        <AccordionSection
          index={5}
          title="Personal Assistant (PA) Team Contacts"
          hint="2 Assistants"
          open={open.has(5)}
          onToggle={() => toggle(5)}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <PaCard
              title="Primary PA (Protocol & Constituency)"
              badge="PA-01"
              path="pa.primary"
              value={values.pa.primary}
              namePlaceholder="Full Name (e.g. Amit Verma)"
              designationPlaceholder="Official Designation (e.g. Chief Liaison Officer)"
              setFieldValue={setFieldValue}
            />
            <PaCard
              title="Secondary PA (Legislative & Media)"
              badge="PA-02"
              path="pa.secondary"
              value={values.pa.secondary}
              namePlaceholder="Full Name (e.g. Sunita Nair)"
              designationPlaceholder="Official Designation (e.g. Media Coordinator)"
              setFieldValue={setFieldValue}
            />
          </div>
        </AccordionSection>

        {/* Section 6 — Bio-Data & Track Record */}
        <AccordionSection
          index={6}
          title="Bio-Data & Track Record"
          hint="Summary, Legislative Record"
          open={open.has(6)}
          onToggle={() => toggle(6)}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Executive Bio-Data Summary">
              <textarea
                name="bioSummary"
                rows={4}
                placeholder="Brief career overview, educational credentials, social service initiatives..."
                value={values.bioSummary}
                onChange={handleChange}
                className={`${fieldCls()} resize-y`}
              />
            </Field>
            <Field label="Political History & Legislative Record">
              <textarea
                name="politicalHistory"
                rows={4}
                placeholder="Previous tenures held, assembly terms, prominent committee roles, key bills drafted..."
                value={values.politicalHistory}
                onChange={handleChange}
                className={`${fieldCls()} resize-y`}
              />
            </Field>
          </div>
        </AccordionSection>

        {/* Section 7 — Entry Review & Verification Status */}
        <AccordionSection
          index={7}
          title="Entry Review & Verification Status"
          hint="Active State, Review Flag"
          open={open.has(7)}
          onToggle={() => toggle(7)}
        >
          <div className="flex flex-col gap-4">
            {/* Entry for Review / Incomplete */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#fde68a]/80 bg-[#fffbeb]/60 p-[17px]">
              <div className="flex items-start gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#fef3c7] text-[#d97706]">
                  <FlagEntryIcon className="size-4" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#00173e]">
                      Entry for Review / Incomplete
                    </span>
                    <span className="rounded border border-[#fde68a] bg-[#fef3c7] px-[9px] py-[3px] text-[10px] font-semibold leading-[15px] text-[#92400e]">
                      Needs Verification
                    </span>
                  </div>
                  <span className="text-xs leading-4 text-[#64748b]">
                    Mark this profile as requiring further data or verification before final gazette
                    approval.
                  </span>
                </div>
              </div>
              <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-[#fde68a] bg-white px-[13px] py-[9px] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
                <input
                  type="checkbox"
                  checked={values.markIncomplete}
                  onChange={(e) => setFieldValue('markIncomplete', e.target.checked)}
                  className="size-4 accent-[#d97706]"
                />
                <span className="text-xs font-medium text-[#78350f]">Mark as Incomplete</span>
              </label>
            </div>

            {/* Account Status */}
            <div className="flex items-center justify-between gap-4 rounded-xl border border-[#dce9fa] bg-[#f0f6fe] px-[21px] py-4">
              <div className="flex items-center gap-2.5">
                <span className="size-2.5 shrink-0 rounded-full bg-[#2563eb]" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-[#1e293b]">Account Status</span>
                  <span className="text-xs text-[#64748b]">
                    User account will be provisioned as{' '}
                    <span className="font-bold text-[#2563eb]">
                      {values.active ? 'Active' : 'Inactive'}
                    </span>
                  </span>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={values.active}
                onClick={() => setFieldValue('active', !values.active)}
                className={[
                  'relative h-6 w-11 shrink-0 rounded-full transition-colors',
                  values.active ? 'bg-[#2563eb]' : 'bg-[#c2c6d6]',
                ].join(' ')}
              >
                <span
                  className={[
                    'absolute top-0.5 size-5 rounded-full bg-white transition-all',
                    values.active ? 'left-[22px]' : 'left-0.5',
                  ].join(' ')}
                />
              </button>
            </div>
          </div>
        </AccordionSection>
      </div>
    </div>
  );
}
